import type {
  DataSourceResponse,
  SourceDetailResponse
} from '../SourceCreate.types';

/**
 * Recursively trims whitespace from all string values in an object or array.
 *
 * **Why trimming is critical:**
 * - Prevents accidental leading/trailing spaces in API requests
 * - Ensures consistent data validation (e.g., "admin " !== "admin")
 * - Avoids backend string matching issues
 * - Improves data quality in the database
 *
 * **Special handling for `directory_path`:**
 * Google Cloud Storage providers support multiple comma-separated directory paths
 * (e.g., "logs/audit/, logs/security/, logs/network/"). This field receives
 * special treatment: each path is trimmed individually while preserving the
 * comma-separated structure.
 *
 * @param obj - Object, array, or primitive value to recursively trim
 * @returns New object/array with all strings trimmed; primitives passed through
 *
 * @example
 * trimObjectValues({ name: "  AWS S3  ", paths: [" log1 ", " log2 "] })
 * // => { name: "AWS S3", paths: ["log1", "log2"] }
 *
 * trimObjectValues({ directory_path: "logs/audit/ , logs/security/ " })
 * // => { directory_path: "logs/audit/,logs/security/" }
 */
export const trimObjectValues = <T>(obj: T): T => {
  // Handle arrays: recursively trim each element
  if (Array.isArray(obj)) {
    return obj.map(trimObjectValues) as unknown as T;
  }

  // Handle objects: recursively trim each property value
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        // Special case: directory_path supports comma-separated values
        // Trim each path segment individually and remove empty segments
        if (key === 'directory_path' && typeof value === 'string') {
          return [
            key,
            value
              .split(',')
              .map((path) => path.trim())
              .filter(Boolean) // Remove empty strings
              .join(',')
          ];
        }

        // Recursively trim nested objects or trim string values
        return [
          key,
          typeof value === 'string' ? value.trim() : trimObjectValues(value)
        ];
      })
    ) as T;
  }

  // Primitives (strings, numbers, booleans) pass through unchanged
  return obj;
};

/**
 * Performs shallow trimming of string values at the top level of a form object.
 *
 * **When to use this vs `trimObjectValues`:**
 * - Use `trimFormValues`: For simple flat form data before submission (faster, shallow)
 * - Use `trimObjectValues`: For nested data structures like data provider configs (thorough, recursive)
 *
 * **Why shallow trimming matters:**
 * This function is called in the dialog submit handler to sanitize top-level form
 * fields (source_name, device_uid, etc.) without the overhead of deep recursion.
 * Nested structures like connection_inputs use `trimObjectValues` separately.
 *
 * @param data - Flat form data object to trim
 * @returns New object with top-level string values trimmed
 *
 * @example
 * trimFormValues({ source_name: "  MySource  ", device_id: 123 })
 * // => { source_name: "MySource", device_id: 123 }
 *
 * // Note: Does NOT trim nested objects
 * trimFormValues({ name: " test ", nested: { value: " keep spaces " } })
 * // => { name: "test", nested: { value: " keep spaces " } }
 */
export const trimFormValues = <T extends Record<string, unknown>>(
  data: T
): T => {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.trim() : value
    ])
  ) as T;
};

/**
 * Extracts the list of Microsoft provider names that are already saved in the database.
 *
 * **Why we need this:**
 * In Edit mode, the form needs to distinguish between:
 * - **Existing providers**: Already configured in the database (don't re-inherit defaults)
 * - **New providers**: Being added during this edit (should inherit defaults)
 *
 * **Business rule impact:**
 * This function enables the inheritance hooks to implement "preserve existing, inherit for new":
 * - `useSiteNameInheritance`: Skips inheritance for existing providers
 * - `useIngestFromInheritance`: Skips inheritance for existing providers
 * - `useCategoriesBySourceType`: Determines whether to show pre-selected categories
 * - `DataSourceRow`: Controls whether the "Active" toggle is enabled on load
 *
 * **Valid provider names:**
 * - O365
 * - MicrosoftDefenderUnified
 * - GraphAPI (from MicrosoftEntraID.GraphAPI)
 * - O365API (from MicrosoftEntraID.O365API)
 *
 * @param selectedSourceDetails - The full source details response from the backend
 * @returns Array of provider names (e.g., ["O365", "GraphAPI"]) that exist in the database
 *
 * @example
 * // Source has O365 and GraphAPI configured
 * getSavedMicrosoftProviderNames(sourceDetails)
 * // => ["O365", "GraphAPI"]
 *
 * // New source (Edit mode, nothing saved yet)
 * getSavedMicrosoftProviderNames(sourceDetails)
 * // => []
 */
export const getSavedMicrosoftProviderNames = (
  selectedSourceDetails: SourceDetailResponse
): string[] => {
  return (selectedSourceDetails?.ms_data_providers ?? [])
    .map((provider: DataSourceResponse) => provider?.connection_inputs?.name)
    .filter((name): name is string => Boolean(name));
};
